import React from 'react';
import type { RfqListItemDto } from '../../lib/rfqApi';
import { generationErrorText } from './generationError';
import { formatDayMonth as fmtDayMonth } from "../../utils/dateFormat";
import { cleanSenderName as cleanName, initialsForName as initials } from './avatarInitials';

interface Props {
  rfq: RfqListItemDto;
  selected: boolean;
  onClick: () => void;
  onDelete?: (id: string) => void;
}

const COLOR_PALETTE = [
  '#E87B7B', // coral-red
  '#9B7BDB', // purple
  '#2D9BA0', // teal
  '#4D8FAC', // steel-blue
  '#E8945A', // orange
  '#5A9DE8', // blue
  '#DB7BB5', // pink
  '#7B8DB5', // slate-blue
];

function colorForKey(key: string) {
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash + key.charCodeAt(i) * 17) % 9973;
  }
  return COLOR_PALETTE[hash % COLOR_PALETTE.length];
}

function formatReceivedAt(dateStr?: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const isToday =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();

  if (isToday) {
    return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', hour12: true });
  }

  const dayName = date.toLocaleDateString(undefined, { weekday: 'short' });
  // dd/mm — this read mm/dd, which is the one thing that must not differ from
  // the rest of the system.
  return `${dayName}, ${fmtDayMonth(date)}`;
}

export const RfqListItem: React.FC<Props> = ({ rfq, selected, onClick, onDelete }) => {
  const badgeText =
    rfq.processingMode === 'auto'
      ? 'Crawled'
      : rfq.processingMode === 'manual_upload'
      ? 'Manual'
      : 'Normal';

  const badgeColor =
    rfq.processingMode === 'auto'
      ? 'bg-blue-100 text-blue-700'
      : rfq.processingMode === 'manual_upload'
      ? 'bg-amber-100 text-amber-700'
      : 'bg-slate-100 text-slate-600';

  const displayName = cleanName(rfq.customerName) || 'Unknown customer';
  const circleColor = colorForKey(rfq._id || displayName || 'Unknown');

  const safeSubject =
    (rfq.subject || '').replace(/\s+/g, ' ').trim().slice(0, 120) || 'No subject';

  return (
    <button
      onClick={onClick}
      className={`group w-full text-left px-4 py-3 border-b border-slate-100 flex gap-3 transition-colors ${
        selected ? 'bg-white shadow-sm' : 'bg-transparent hover:bg-white'
      }`}
    >
      {/* Avatar */}
      <div className="flex-shrink-0 pt-0.5">
        <div
          className="h-9 w-9 rounded-full flex items-center justify-center text-xs font-bold text-white select-none"
          style={{ backgroundColor: circleColor }}
        >
          {initials(rfq.customerName)}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Row 1: name + date + delete */}
        <div className="flex items-center justify-between gap-1">
          <div className="text-[13px] font-semibold text-slate-800 truncate">
            {displayName}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0 ml-1">
            <span className="text-xs text-slate-400 whitespace-nowrap">
              {formatReceivedAt(rfq.receivedAt)}
            </span>
            {onDelete && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(rfq._id);
                }}
                className="opacity-0 group-hover:opacity-100 p-0.5 rounded text-slate-300 hover:text-red-400 hover:bg-red-50 transition-all"
                title="Delete RFQ"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Row 2: subject */}
        <div className="text-xs text-slate-500 mt-0.5 truncate leading-snug">
          {safeSubject}
        </div>

        {/* Row 3: badge */}
        <div className="flex items-center justify-end gap-1 mt-1.5">
          {rfq.clientLinkError && (
            <span
              className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700"
              title={`Client not auto-linked: ${rfq.clientLinkError}`}
            >
              ⚠ Client not linked
            </span>
          )}
          {rfq.status === 'error' && rfq.generationError?.code && (
            <span
              className="px-2 py-0.5 rounded-full text-xs font-semibold bg-rose-100 text-rose-700"
              title={generationErrorText(rfq.generationError.code)}
            >
              Quotation failed
            </span>
          )}
          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${badgeColor}`}>
            {badgeText}
          </span>
        </div>
      </div>
    </button>
  );
};
